import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Checkbox } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import Table from '@material-ui/core/Table';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getServiceEntity } from '../helper';

const useStyles = makeStyles(() => ({
  summaryText: {
    color: '#000',
  },
  tableBody: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      padding: '0',
    },
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  title: {
    marginBottom: '5px',
  },
}));

const RecurringPlanSummary = ({
  datesToCreate,
  setDatesToCreate = () => {},
  unavailableDates,
  datesWithConflicts = [],
  isCompact = false,
  showConflictLoadingIndicator = false,
}) => {
  const classes = useStyles();

  const [formattedDateList, setFormattedDateList] = useState([]);
  const [expandUnavailableDates, setExpandUnavailableDates] = useState(false);
  const [expandIncompatibleDates, setExpandIncompatibleDates] = useState(false);

  useEffect(() => {
    setFormattedDateList(
      datesToCreate.map((d) => moment(d).format('YYYY-MM-DD')),
    );
  }, [datesToCreate]);

  return (
    <Grid item xs={12} className={classes.summaryText}>
      <Typography variant="h6" className={classes.title}>
        Scheduled Appointments:{' '}
        {datesToCreate.length > 0 ? datesToCreate.length : 'None'}
        {showConflictLoadingIndicator && (
          <CircularProgress size={14} style={{ marginLeft: '5px' }} />
        )}
      </Typography>
      {unavailableDates.length > 0 && (
        <div>
          <Grid container xs={isCompact ? 12 : 8}>
            <Grid item xs={11}>
              <Tooltip
                title={`${getServiceEntity(
                  false,
                  'capitalization',
                )} is not available this day. The appointment will not be created.`}
              >
                <Typography variant="subtitle2">
                  Unavailable Dates: {unavailableDates.length}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item>
              {expandUnavailableDates ? (
                <KeyboardArrowDownIcon
                  className={classes.cursorPointer}
                  onClick={() => setExpandUnavailableDates(false)}
                />
              ) : (
                <KeyboardArrowRightIcon
                  className={classes.cursorPointer}
                  onClick={() => setExpandUnavailableDates(true)}
                />
              )}
            </Grid>
          </Grid>
          {expandUnavailableDates &&
            unavailableDates.map((d) => (
              <Typography
                style={
                  !formattedDateList.includes(moment(d).format('YYYY-MM-DD'))
                    ? { fontStyle: 'italic', color: '#959393' }
                    : {}
                }
              >
                {moment(d).format(
                  `MMMM DD${
                    moment(d).format('YYYY') !== moment().format('YYYY')
                      ? ', YYYY'
                      : ''
                  }`,
                )}
              </Typography>
            ))}
        </div>
      )}
      {datesWithConflicts.length > 0 && (
        <div>
          <Grid container xs={isCompact ? 12 : 8}>
            <Grid item xs={11}>
              <Tooltip title="This appointment does not fit within your scheduling rules on this day. You can choose to include or remove these days.">
                <Typography variant="subtitle2">
                  Dates With Conflicts: {datesWithConflicts.length}{' '}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item>
              {expandIncompatibleDates ? (
                <KeyboardArrowDownIcon
                  className={classes.cursorPointer}
                  onClick={() => setExpandIncompatibleDates(false)}
                />
              ) : (
                <KeyboardArrowRightIcon
                  className={classes.cursorPointer}
                  onClick={() => setExpandIncompatibleDates(true)}
                />
              )}
            </Grid>
            {expandIncompatibleDates && (
              <Grid item xs={12}>
                <Table size="small">
                  <TableBody className={classes.tableBody}>
                    {datesWithConflicts.map((d) => (
                      <TableRow>
                        <TableCell>
                          <Typography
                            className={classes.checkboxList}
                            style={
                              !formattedDateList.includes(d.date)
                                ? { fontStyle: 'italic', color: '#959393' }
                                : {}
                            }
                          >
                            <strong>
                              {moment(d.date).format(
                                `MMM D${
                                  moment(d.date).format('YYYY') !==
                                  moment().format('YYYY')
                                    ? ", 'YY"
                                    : ''
                                }`,
                              )}
                            </strong>
                            {' - '}
                            <span style={{ fontSize: '.9rem' }}>
                              {d.short_reason}
                            </span>
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <span style={{ fontSize: '12px' }}>
                            {formattedDateList.includes(d.date)
                              ? 'Scheduled'
                              : 'Removed'}
                          </span>
                          <Checkbox
                            onClick={() => {
                              const index = formattedDateList.indexOf(d.date);
                              // If the date exists in the datesToCreate array, remove it. Otherwise, add back in.
                              if (index >= 0) {
                                const newDateList = datesToCreate.slice();
                                newDateList.splice(index, 1);
                                setDatesToCreate(newDateList);
                              } else {
                                const newDateList = datesToCreate.slice();
                                newDateList.push(d.date);
                                newDateList.sort((a, b) => (a > b ? 1 : -1));
                                setDatesToCreate(newDateList);
                              }
                            }}
                            checked={formattedDateList.includes(d.date)}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </Grid>
  );
};

export default RecurringPlanSummary;
